import { useState } from 'react'
import { db, type DiningTable } from '../lib/db'
import { tableApi } from '../lib/api'
import { useApp } from '../lib/store'
import { whenText } from '../lib/money'
import { quickReserveTimes, toLocalInput } from '../lib/hooks'
import { Sheet } from './Sheet'

interface Props {
  table: DiningTable
  onClose: () => void
}

export function ReserveSheet({ table, onClose }: Props) {
  const say = useApp((s) => s.say)
  const quick = quickReserveTimes()

  const [when, setWhen] = useState(() => toLocalInput(table.reservedAt))
  const [name, setName] = useState(table.reservedName ?? '')
  const [phone, setPhone] = useState('')
  const [party, setParty] = useState(table.reservedPartySize || 2)
  const [note, setNote] = useState(table.reservedNote ?? '')

  async function save() {
    // datetime-local không mang múi giờ; Date() đọc theo giờ máy rồi ISO hoá.
    const at = new Date(when)
    if (Number.isNaN(at.getTime())) {
      say('Giờ đặt bàn chưa hợp lệ')
      return
    }

    const patch = {
      reservedAt: at.toISOString(),
      reservedName: name.trim() || null,
      reservedPartySize: party,
      reservedNote: note.trim() || null,
    }
    await db.diningTables.update(table.id, patch)

    try {
      await tableApi.reserve(table.id, {
        reservedAt: patch.reservedAt,
        name: name.trim(),
        phone: phone.trim(),
        partySize: party,
        note: note.trim(),
      })
      say(`Đã đặt ${table.name} lúc ${whenText(patch.reservedAt)}`)
    } catch {
      say('Chưa có mạng — đã ghi trên máy này')
    }
    onClose()
  }

  async function clear() {
    await db.diningTables.update(table.id, {
      reservedAt: null,
      reservedName: null,
      reservedPartySize: 0,
      reservedNote: null,
    })
    try {
      await tableApi.reserve(table.id, { reservedAt: null })
    } catch {
      /* huỷ đặt bàn đã ghi trên máy, đẩy lên sau */
    }
    say(`Đã bỏ đặt ${table.name}`)
    onClose()
  }

  return (
    <Sheet title={`Đặt trước · ${table.name}`} onClose={onClose}>
      <div className="field">
        <span>Mấy giờ khách tới</span>
        <div className="chip-row">
          {quick.map((q) => (
            <button
              key={q.label}
              type="button"
              className="chip"
              aria-pressed={toLocalInput(q.value.toISOString()) === when}
              onClick={() => setWhen(toLocalInput(q.value.toISOString()))}
            >
              {q.label}
            </button>
          ))}
        </div>
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
      </div>

      <div className="field-row">
        <label className="field">
          <span>Tên khách</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Anh Tuấn" maxLength={120} />
        </label>
        <label className="field">
          <span>Mấy người</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={100}
            value={party}
            onChange={(e) => setParty(Number(e.target.value))}
          />
        </label>
      </div>

      <label className="field">
        <span>Số điện thoại</span>
        <input
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="0912 345 678"
        />
      </label>

      <label className="field">
        <span>Ghi chú</span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Kê thêm ghế, ngồi gần cửa sổ…"
          maxLength={200}
        />
      </label>

      <div className="sheet-actions">
        {table.reservedAt && (
          <button type="button" className="btn btn-danger-ghost" onClick={clear}>
            Bỏ đặt
          </button>
        )}
        <button type="button" className="btn btn-brand grow" onClick={save}>
          Lưu đặt bàn
        </button>
      </div>
    </Sheet>
  )
}
